import type NativeArray from '@ember/array/-private/native-array';
import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import type AbilitiesService from 'ember-can/services/abilities';
import type { Task } from 'ember-concurrency';
import { all, dropTask } from 'ember-concurrency';
import type FlowModel from 'garaje/models/flow';
import type SignInFieldModel from 'garaje/models/sign-in-field';
import type SignInFieldPageModel from 'garaje/models/sign-in-field-page';
import type TenantModel from 'garaje/models/tenant';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type StateService from 'garaje/services/state';
import type VisitorsOnboardingService from 'garaje/services/visitors-onboarding';
import type { RecordArray } from 'garaje/utils/type-utils';

export interface VisitorsInvitesRouteModel {
  connectedTenants: RecordArray<TenantModel>;
  loadFlowDataTask?: Task<FlowsTaskResponse, [{ isConnectedToProperty: boolean; locationId: string }]>;
}

export interface FlowsTaskResponse {
  flows?: FlowModel[];
  customFields?: NativeArray<SignInFieldModel>[];
  signInFieldPages?: SignInFieldPageModel[];
  locationId?: string;
}

class VisitorsInvitesRoute extends Route {
  @service declare abilities: AbilitiesService;
  @service declare state: StateService;
  @service declare visitorsOnboarding: VisitorsOnboardingService;
  @service declare router: RouterService;
  @service declare featureFlags: FeatureFlagsService;

  titleToken = 'Invites';

  beforeModel(): Promise<unknown> | void {
    const { currentLocation, vrSubscription } = this.state;
    if (!currentLocation?.preRegistrationEnabled) {
      // @ts-ignore
      return this.router.transitionTo('visitors.entries.index');
    }

    if (!(isPresent(vrSubscription) && this.abilities.can('visit dashboard'))) {
      if (this.abilities.can('visit dashboard')) {
        void this.router.transitionTo('visitors.start-trial');
      } else if (this.abilities.can('visit billing')) {
        void this.router.transitionTo('billing');
      } else if (this.abilities.can('visit security-desk')) {
        void this.router.transitionTo('security-desk');
      } else if (this.abilities.can('visit security')) {
        void this.router.transitionTo('security');
      } else {
        void this.router.transitionTo('unauthorized');
      }
    }
  }

  async model(): Promise<VisitorsInvitesRouteModel> {
    const { currentLocation } = this.state;

    await this.visitorsOnboarding.loadSteps();

    const connectedTenants = await currentLocation.getPropertyConnections();
    const isConnectedToProperty = <number>connectedTenants.length > 0;

    void this.loadFlowDataTask.perform({
      isConnectedToProperty,
      locationId: currentLocation.id,
    });

    return { connectedTenants, loadFlowDataTask: this.loadFlowDataTask };
  }

  redirect(_model: VisitorsInvitesRouteModel, transition: Transition): void {
    this.visitorsOnboarding.gateRoute(this, transition);
  }

  loadFlowDataTask = dropTask(
    async (options: { isConnectedToProperty: boolean; locationId: string }): Promise<FlowsTaskResponse> => {
      const flows = await this.state.loadFlows({
        includePropertyFlows: options.isConnectedToProperty, // if this location is connected to a property, include flows added by the property
        locationId: options.locationId,
        reload: false,
        excludeEmployeeFlows: this.featureFlags.isEnabled('exclude-workplace-visits'),
      });

      const signInFieldPages = await all(flows.map((flow) => flow.signInFieldPage));
      const customFields = await all(signInFieldPages.map((signInFieldPage) => signInFieldPage?.customFields));

      return {
        flows,
        customFields,
        signInFieldPages,
        locationId: options.locationId,
      };
    },
  );
}

export default VisitorsInvitesRoute;
